import { Helmet } from "react-helmet-async"
import React, { useCallback, useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { Container } from "@mantine/core"
import { ArticleDetailsSkeleton, ArticleDetails } from "entities/article"
import { PageTitle } from "shared/ui/PageTitle"
import { type Article } from "shared/types/article"
import { getArticleById } from "shared/api/articles"
import { ErrorResult } from "../../shared/ui/ErrorResult"
import { API_URL } from "../../shared/config"
import { categoryNames } from "../../shared/lib/constants"

// ----------------------------------------------------------------------

export function ArticleDetailsPage(): React.JSX.Element {
  const { articleId = "" } = useParams<{ articleId: string }>()
  const url = new URL(window.location.href)

  const [ article, setArticle ] = useState<Article | null>(null)
  const [ isLoading, setIsLoading ] = useState<boolean>(false)
  const [ error, setError ] = useState<string | null>(null)

  const loadArticle = useCallback(async () => {
    setIsLoading(true)

    try {
      const { data } = await getArticleById(articleId)
      setArticle(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Непредвиденная ошибка")
    } finally {
      setIsLoading(false)
    }
  }, [ articleId ])

  useEffect(() => {
    void loadArticle()
  }, [ loadArticle ])

  if (isLoading) {
    return <ArticleDetailsSkeleton/>
  }

  const title = article ? article.title : "Статья"
  const description = article ? article.description : "Описание статьи"
  const imageContent = article ? `${API_URL}/media/${article.cover}` : undefined
  const keyWords = article ? [ categoryNames[article.category], article.keywords ?? "" ].join(", ") : undefined

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta property="og:title" content={title}/>

        <meta name="description"
              content={description}/>
        <meta property="og:description"
              content={description}/>
        <meta property="og:image" content={imageContent}/>

        <meta property="og:url" content={url.href}/>
        <link rel="canonical" href={url.href}/>
        <meta property="og:site_name" content={url.host}/>
        <meta name="robots" content="index, follow"/>

        {keyWords && <meta name="keywords" content={keyWords}/>}
      </Helmet>

      <Container size="xl" mt={48} mb={64}>
        <PageTitle title={article ? article.title : "Статья"}/>

        {error ? <ErrorResult errorMessage={error}/> : <ArticleDetails {...article!} />}
      </Container>
    </>
  )
}
